import Directions from "./directions";
import Route from "./route";
import Agency from "./agency";
import Stop from "./stop";
import Utilities from "./utilities";

// stores the current tabs and the current panel/pane for each of the tabs
const pluginDisplay = {
	'tab': 'bus_routes',
	'bus_routes': {
		panel: 'overview_bus_routes',
		pane: ''
	},
	'trip_planner': {
		panel: 'overview_trip_planner',
		pane: '',
		extra: [null, null, null, null]
	},
	'alerts': {
		panel: 'overview_alerts',
		pane: ''
	}
};

const History = {
	init: async ( tm ) => {
		await History.initPluginDisplayState( tm );

		History.maybeTriggerTabChangeEventOnLoad( tm );

		const tablist = tm.$sidebar.querySelector( 'ul' );
		window.addEventListener( "popstate", ( e ) => {
				if ( !e.state || !e.state.tab ) return;
				History.maybeChangeTabOnPopstate( e, tm, tablist );
				History.maybeChangePanelOnPopstate( e, tm )
			}
		);
	},

	initPluginDisplayState: async ( tm ) => {
		return new Promise( resolve => {
			const params = new URLSearchParams( location.search ),
				tab = params.get( "tab" ) ? params.get( "tab" ) : "bus_routes";

			pluginDisplay.tab = tab;

			switch ( tab ) {
				case 'alerts':
					let alertsForAgency = params.get( "agency" );
					if ( alertsForAgency ) {
						pluginDisplay[tab].panel = 'alert_detail';
						pluginDisplay[tab].pane = 'agency-alerts|' + alertsForAgency;
					}
					break;
				case 'trip_planner':
					let origin = params.get( "start" ),
						destination = params.get( "dest" ),
						depart = params.get( "depart" ),
						arrive = params.get( "arrive" );
					if ( origin && destination && (depart || arrive) ) {
						pluginDisplay[tab].panel = 'directions_detail';
						pluginDisplay[tab].extra = Directions.parseDirectionsForm();
					}
					break;
				case 'bus_routes':
					const agencyId = params.get( "agency" ) ? parseInt( params.get( "agency" ), 10 ) : null,
						routeId = params.get( "route" ) ? parseInt( params.get( "route" ), 10 ) : null,
						stopId = params.get( "stop" ) ? parseInt( params.get( "stop" ), 10 ) : null;

					if ( agencyId ) {
						Agency.updateVisibleAgency( tm, agencyId );
						pluginDisplay[tab].panel = 'agency_detail';
						pluginDisplay[tab].pane = 'agency|' + agencyId;
						if ( routeId ) {
							Route.updateVisibleRoute( tm, routeId );
							pluginDisplay[tab].panel = 'route_detail';
							pluginDisplay[tab].pane = 'route|' + routeId;
							if ( stopId ) {
								Stop.updateVisibleStop( tm, stopId );
								pluginDisplay[tab].panel = 'stop_detail';
								pluginDisplay[tab].pane = 'stop|' + stopId;
							}
						}
					}

					break;
			}

			// store initial state in history
			history.replaceState( pluginDisplay, '' );

			resolve();
		} );
	},

	maybeChangeTabOnPopstate: ( e, tm, tablist ) => {
		const tab = e.state.tab,
			currentTab = tablist.querySelector( '[aria-selected="true"]' ),
			newTab = tablist.querySelector( '[href="?tab=' + tab + '"]' );

		if ( currentTab && newTab && currentTab !== newTab ) {
			const eventDetail = {
					oldTab: currentTab,
					newTab: newTab,
					dontPushHistory: true
				},
				eventTab = new CustomEvent( 'tm__change-tab', {detail: eventDetail} );
			tm.$sidebar.dispatchEvent( eventTab );
		}
	},

	maybeChangePanelOnPopstate: ( e, tm ) => {
		const tab = e.state.tab,
			eventDetail = {
				section: 'tab-' + tab,
				panel: e.state[tab].panel,
				pane: e.state[tab].pane,
				transition: false,
				dontPushHistory: true
			},
			event = new CustomEvent( 'tm__panel-button-clicked', {detail: eventDetail} );
		if ( eventDetail.panel ) {
			tm.$sidebar.dispatchEvent( event );
		}

		if ( tab === 'trip_planner' && eventDetail.panel === 'directions_detail' ) {
			Directions.displayRoute( tm, e.state[tab].extra );
		}
	},

	maybePushTabState: ( tm, e, newTab ) => {
		if ( Utilities.deepEqual( history.state, {} ) ) {
			// no idea why, but sometimes the initial state gets cleared out sometime after it's saved
			// (it's probably only necessary to do this check on maybePushPanelState(), not here)
			history.replaceState( pluginDisplay, '' );
		}

		const params = new URLSearchParams( newTab.getAttribute( 'href' ) ),
			tab = params.get( "tab" );
		if ( !tab ) return;

		pluginDisplay.tab = tab;

		// if this event was triggered by hitting the back button, don't push this event
		if ( e.detail.dontPushHistory ) return;

		// don't push if the state didn't actually change
		if ( !Utilities.deepEqual( history.state, pluginDisplay ) ) {
			history.pushState( pluginDisplay, '', History.getUrl( tm ) );
		}
	},

	maybePushPanelState: ( tm, detail ) => {
		if ( Utilities.deepEqual( history.state, {} ) ) {
			// no idea why, but sometimes the initial state gets cleared out sometime after it's saved
			history.replaceState( pluginDisplay, '' );
		}

		const tab = pluginDisplay.tab;
		pluginDisplay[tab].panel = detail.panel;
		pluginDisplay[tab].pane = detail.pane;

		if ( tab === 'trip_planner' && detail.panel === 'directions_detail' ) {
			pluginDisplay[tab].extra = Directions.parseDirectionsForm();
		}

		document.getElementById( 'tab-' + pluginDisplay.tab ).focus( {preventScroll: true} );

		// if this event was triggered by hitting the back button, don't push this event
		if ( detail.dontPushHistory ) return;

		// don't push if the state didn't actually change
		if ( !Utilities.deepEqual( history.state, pluginDisplay ) ) {
			history.pushState( pluginDisplay, '', History.getUrl( tm ) );
		}
	},

	getUrl: ( tm ) => {
		const tab = pluginDisplay.tab;
		let url = '?tab=' + tab;

		if ( tab === 'alerts' && pluginDisplay[tab].panel === 'alert_detail' ) {
			url += '&agency=' + pluginDisplay[tab].pane.substring( 14 );
		}

		if ( tab === 'trip_planner' && pluginDisplay[tab].panel === 'directions_detail' ) {
			const [origin, destination, timeKey, timeVal] = Directions.parseDirectionsForm();
			const key = timeKey === 'departureTime' ? 'depart' : 'arrive';
			const timestamp = Math.round( timeVal.getTime() / 1000 );
			url += '&start=' + encodeURIComponent( origin );
			url += '&dest=' + encodeURIComponent( destination );
			url += '&' + key + '=' + timestamp;
		}

		if ( tab === 'bus_routes' ) {
			url = tm.visible.agencyId ? '?agency=' + tm.visible.agencyId : window.location.pathname;
			url += tm.visible.routeId ? '&route=' + tm.visible.routeId : '';
			url += tm.visible.stopId ? '&stop=' + tm.visible.stopId : '';
		}

		return url;
	},

	maybeTriggerTabChangeEventOnLoad: ( tm ) => {
		if ( pluginDisplay.tab === 'bus_routes' ) return;

		const panels = tm.$sidebar.querySelectorAll( 'section[id^="tab"]' ),
			index = [...panels].findIndex( elem => elem.id === 'tab-' + pluginDisplay.tab );
		if ( index && index >= 0 ) {
			const event = new CustomEvent( 'tm__change-tab', {detail: {oldIndex: 0, newIndex: index, dontPushHistory: true}} );
			tm.$sidebar.dispatchEvent( event );
		}
	},

	triggerPanelClickEventOnLoad: async ( tm ) => {
		const tab = pluginDisplay.tab;

		// restore routes, because they are used for stops
		if ( tab === 'bus_routes' ) {
			if ( pluginDisplay[tab].panel === 'route_detail' ) {
				await Route.renderPaneRouteDetail( tm );
			}
			if ( pluginDisplay[tab].panel === 'stop_detail' ) {
				Route.showRouteAndStops( tm );
				await Route.renderPaneRouteDetail( tm );
			}
		}

		// restore directions
		if ( tab === 'trip_planner' && pluginDisplay[tab].panel === 'directions_detail' ) {
			Directions.displayRoute( tm, pluginDisplay[tab].extra );
		}
		const properties = {
				section: 'tab-' + tab,
				panel: pluginDisplay[tab].panel,
				pane: pluginDisplay[tab].pane,
				dontPushHistory: true,
				transition: false
			},
			event = new CustomEvent( 'tm__panel-button-clicked', {detail: properties} );
		tm.$sidebar.dispatchEvent( event );
	},
}

export default History;
